import { translate, translateRich, useLocale } from "../localization";
// GuideEditor.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Container, Box, Paper, Typography, CircularProgress, Tooltip, Menu, MenuItem } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import InsightEditor from "./InsightEditor";
import ExperiencesAPI from "../services/ExperiencesAPI";
import SystemOptionsDropdown from "./SystemOptionsDropdown";
import { useContent } from "../context/ContentContext";
import { useExperiences } from "../context/ExperiencesContext";
import { AppButton, AppTextField } from "./ui";
import ElementLabelsDialog from "./guides/ElementLabelsDialog";
import {
  cloneGuideActionButtonLabels,
  cloneGuideElementLabels,
  cloneGuideEnvironmentLabels,
  extractSystemGuideLabels,
  reconcileGuideActionButtonLabels,
  reconcileGuideElementLabels,
  reconcileGuideEnvironmentLabels,
  updateRecoveryDestinationsForEnvironmentLabels,
  validateGuideActionButtonLabels,
  validateGuideElementLabels,
  validateGuideEnvironmentLabels,
} from "../utility/guideElementLabels";

import CloseIcon from "@mui/icons-material/Close";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import IconButton from "@mui/material/IconButton";
import { downloadGuideDocx } from "../utility/guideDocxExport";
import { getPreviousOrdinaryInsight, isIdleInsight, orderGuideInsights } from "../utility/guideInsightOrder";
import "./GuideEditorElementLabels.scss";

// Helper to generate a UUID
const generateId = () => crypto.randomUUID();
const createDefaultInsightCompleteTrigger = () => ({
  name: "",
  insightTriggerType: 2,
  triggerId: "",
});
const GUIDE_READING_WORDS_PER_MINUTE = 140;
const GUIDE_BASE_SECONDS_PER_INSIGHT = 3;
const GUIDE_ENVIRONMENT_CHANGE_SECONDS = 15;
const GUIDE_ASSESSMENT_TIME_MULTIPLIER = 1.5;
const tooltipSurfaceProps = {
  tooltip: {
    sx: {
      backgroundColor: "rgba(19, 24, 33, 0.96)",
      color: "#ffffff",
      borderRadius: "var(--radius-md, 8px)",
      px: 1.5,
      py: 1.25,
      fontSize: 12,
      lineHeight: 1.45,
      maxWidth: 280,
      boxShadow: "0 10px 24px rgba(0, 0, 0, 0.24)",
    },
  },
  arrow: {
    sx: {
      color: "rgba(19, 24, 33, 0.96)",
    },
  },
};

const getFirstTextValue = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const getFirstNumberValue = (...values) => {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
};

const getSystemEnvironmentEntries = (system) => {
  if (!system) {
    return [];
  }

  const entriesById = new Map();

  const upsertEnvironmentEntry = (environmentEntry, fallbackId) => {
    if (!environmentEntry && !fallbackId) {
      return;
    }

    if (typeof environmentEntry === "string") {
      const environmentId = getFirstTextValue(environmentEntry, fallbackId);
      if (environmentId) {
        const existingEntry = entriesById.get(environmentId) || {};
        entriesById.set(environmentId, {
          id: environmentId,
          name: existingEntry.name || "",
          instanceId: existingEntry.instanceId ?? null,
        });
      }
      return;
    }

    if (environmentEntry && typeof environmentEntry === "object") {
      const environmentId = getFirstTextValue(
        environmentEntry.id,
        environmentEntry.environmentId,
        environmentEntry.environmentID,
        fallbackId
      );
      const environmentName = getFirstTextValue(
        environmentEntry.environmentName,
        environmentEntry.name,
        environmentEntry.metadata?.name,
        environmentEntry.title,
        environmentEntry.metadata?.title
      );
      const instanceId = getFirstNumberValue(
        environmentEntry.instanceID,
        environmentEntry.instanceId
      );

      if (!environmentId) {
        return;
      }

      const existingEntry = entriesById.get(environmentId) || {};
      entriesById.set(environmentId, {
        id: environmentId,
        name: environmentName || existingEntry.name || "",
        instanceId: instanceId ?? existingEntry.instanceId ?? null,
      });
      return;
    }

    if (fallbackId) {
      const existingEntry = entriesById.get(fallbackId) || {};
      entriesById.set(fallbackId, {
        id: fallbackId,
        name: existingEntry.name || "",
        instanceId: existingEntry.instanceId ?? null,
      });
    }
  };

  if (Array.isArray(system.environmentIDs)) {
    system.environmentIDs.forEach((environmentId) => {
      upsertEnvironmentEntry(environmentId);
    });
  }

  if (Array.isArray(system.environments)) {
    system.environments.forEach((environmentEntry) => {
      upsertEnvironmentEntry(environmentEntry);
    });
  } else if (system.environments && typeof system.environments === "object") {
    Object.entries(system.environments).forEach(([environmentId, environmentEntry]) => {
      upsertEnvironmentEntry(environmentEntry, environmentId);
    });
  }

  if (Array.isArray(system.environmentDatas)) {
    system.environmentDatas.forEach((environmentEntry) => {
      upsertEnvironmentEntry(environmentEntry);
    });
  } else if (system.environmentDatas && typeof system.environmentDatas === "object") {
    Object.entries(system.environmentDatas).forEach(([environmentId, environmentEntry]) => {
      upsertEnvironmentEntry(environmentEntry, environmentId);
    });
  }

  return Array.from(entriesById.values());
};

const resolveSystemStartEnvironment = (system) => {
  if (!system) {
    return { id: "", name: "" };
  }

  const environmentEntries = getSystemEnvironmentEntries(system);
  const environmentById = new Map(environmentEntries.map((entry) => [entry.id, entry]));
  const environmentByInstanceId = new Map(
    environmentEntries
      .filter((entry) => entry.instanceId !== null)
      .map((entry) => [entry.instanceId, entry])
  );

  const rawStartEnvironment = [
    system.currentEnvironmentId,
    system.currentEnvironmentID,
    system.currentenvironmentId,
    system.currentenvironmentID,
    system.startEnvironmentId,
    system.startEnvironmentID,
    system.startenvironmentId,
    system.startenvironmentID,
    system.entryEnvironmentId,
    system.entryEnvironmentID,
    system.defaultEnvironmentId,
    system.defaultEnvironmentID,
    system.metadata?.currentEnvironmentId,
    system.metadata?.currentEnvironmentID,
    system.metadata?.startEnvironmentId,
    system.metadata?.startEnvironmentID,
    system.metadata?.entryEnvironmentId,
    system.metadata?.entryEnvironmentID,
    system.startEnvironment,
    system.startenvironment,
    system.entryEnvironment,
    system.defaultEnvironment,
    system.metadata?.currentEnvironment,
    system.metadata?.startEnvironment,
    system.metadata?.entryEnvironment,
    system.metadata?.defaultEnvironment,
  ].find(Boolean);

  if (rawStartEnvironment && typeof rawStartEnvironment === "object") {
    const id = getFirstTextValue(
      rawStartEnvironment.id,
      rawStartEnvironment.environmentId,
      rawStartEnvironment.environmentID
    );
    const name = getFirstTextValue(
      rawStartEnvironment.environmentName,
      rawStartEnvironment.name,
      rawStartEnvironment.metadata?.name,
      rawStartEnvironment.title
    );

    if (id) {
      return { id, name };
    }

    const instanceId = getFirstNumberValue(
      rawStartEnvironment.instanceID,
      rawStartEnvironment.instanceId
    );
    if (instanceId !== null && environmentByInstanceId.has(instanceId)) {
      const matchedEnvironment = environmentByInstanceId.get(instanceId);
      return {
        id: matchedEnvironment.id,
        name: matchedEnvironment.name || name || "",
      };
    }
  }

  const startEnvironmentId = getFirstTextValue(rawStartEnvironment);
  if (startEnvironmentId) {
    const matchedEnvironment = environmentById.get(startEnvironmentId);
    return {
      id: startEnvironmentId,
      name: matchedEnvironment?.name || "",
    };
  }

  return environmentEntries[0] || { id: "", name: "" };
};

const createDefaultEnvironmentVisitedTrigger = (system) => {
  const startEnvironment = resolveSystemStartEnvironment(system);
  return {
    name: startEnvironment.name || "",
    insightTriggerType: 1,
    triggerId: startEnvironment.id || "",
  };
};

const createBlankEnvironmentVisitedTrigger = () => ({
  name: "",
  insightTriggerType: 1,
  triggerId: "",
});

const createInsightDraft = (defaultTrigger) => ({
  id: generateId(),
  name: "",
  text: "",
  answerKey: "",
  insightType: "",
  dismissable: false,
  insightTriggers: [defaultTrigger],
  startConditions: [],
  startActions: [],
  endConditions: [],
});

const outlineInsightTypeMarkerMap = {
  0: {
    kind: "text",
    value: "i",
    color: "var(--color-action-warning, #ef6c00)",
    textSx: {
      fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
      fontStyle: "italic",
      fontWeight: 700,
      fontSize: "1.05em",
      transform: "translateY(-0.02em)",
    },
  },
  1: { kind: "icon", value: MenuBookOutlinedIcon, color: "var(--color-action-info, #1565c0)" },
  2: { kind: "text", value: "?", color: "var(--color-action-success, #2e7d32)" },
};

const truncateInlineLabel = (value = "", maxLength = 36) => {
  const normalized = String(value).replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "";
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
};

const getEnvironmentVisitedTrigger = (insight) =>
  Array.isArray(insight?.insightTriggers)
    ? insight.insightTriggers.find((trigger) => Number(trigger?.insightTriggerType) === 1)
    : null;

const hasInsightCompleteTrigger = (insight) =>
  Array.isArray(insight?.insightTriggers) &&
  !isIdleInsight(insight) &&
  insight.insightTriggers.some((trigger) => Number(trigger?.insightTriggerType) === 2);

const buildGuideOutlineGroups = (insights = [], environments = []) => {
  const environmentNameById = new Map(
    (Array.isArray(environments) ? environments : []).map((environment) => [environment.id, environment.name])
  );
  const groups = [];
  let currentGroup = null;

  insights.forEach((insight, index) => {
    const environmentTrigger = getEnvironmentVisitedTrigger(insight);
    const environmentId = environmentTrigger?.triggerId || "";
    const environmentName = environmentNameById.get(environmentId) || environmentTrigger?.name || "";

    if (environmentTrigger || !currentGroup || (isIdleInsight(insight) && !isIdleInsight(insights[index - 1]))) {
      currentGroup = {
        key: environmentId ? `${environmentId}-${index}` : `group-${index}`,
        environmentId,
        environmentName: isIdleInsight(insight) ? translate("Idle insights") : environmentName || translate("Guide Start"),
        items: [],
      };
      groups.push(currentGroup);
    }

    currentGroup.items.push({
      id: insight.id,
      index,
      label: truncateInlineLabel(getFirstTextValue(insight?.name, insight?.text), 40) || translate("Untitled insight"),
      isNested: !environmentTrigger && hasInsightCompleteTrigger(insight),
      insightType: Number.isFinite(Number(insight?.insightType)) ? Number(insight.insightType) : null,
    });
  });

  return groups;
};

const getGuideGroupBoundaries = (insights = []) => {
  const groups = [];
  let currentGroup = null;

  insights.forEach((insight, index) => {
    const environmentTrigger = getEnvironmentVisitedTrigger(insight);

    if (environmentTrigger || !currentGroup || (isIdleInsight(insight) && !isIdleInsight(insights[index - 1]))) {
      if (currentGroup) {
        currentGroup.endIndexExclusive = index;
      }

      currentGroup = {
        startIndex: index,
        endIndexExclusive: insights.length,
      };
      groups.push(currentGroup);
    }
  });

  return groups;
};

const getDraggedInsightBlock = (insights = [], sourceIndex) => {
  const sourceInsight = insights[sourceIndex];

  if (!sourceInsight) {
    return {
      startIndex: sourceIndex,
      endIndexExclusive: sourceIndex,
      insights: [],
      isGroupedMove: false,
    };
  }

  if (!getEnvironmentVisitedTrigger(sourceInsight)) {
    return {
      startIndex: sourceIndex,
      endIndexExclusive: sourceIndex + 1,
      insights: [sourceInsight],
      isGroupedMove: false,
    };
  }

  let endIndexExclusive = sourceIndex + 1;

  while (endIndexExclusive < insights.length) {
    const nextInsight = insights[endIndexExclusive];

    if (getEnvironmentVisitedTrigger(nextInsight) || !hasInsightCompleteTrigger(nextInsight)) {
      break;
    }

    endIndexExclusive += 1;
  }

  return {
    startIndex: sourceIndex,
    endIndexExclusive,
    insights: insights.slice(sourceIndex, endIndexExclusive),
    isGroupedMove: endIndexExclusive > sourceIndex + 1,
  };
};

const normalizeGroupedDragDestinationIndex = (insights = [], sourceIndex, rawDestinationIndex) => {
  if (rawDestinationIndex === null || rawDestinationIndex === undefined) {
    return null;
  }

  const sourceInsight = insights[sourceIndex];
  if (!getEnvironmentVisitedTrigger(sourceInsight)) {
    return rawDestinationIndex;
  }

  const originalDestinationIndex =
    rawDestinationIndex > sourceIndex ? rawDestinationIndex + 1 : rawDestinationIndex;
  const groups = getGuideGroupBoundaries(insights);
  const containingGroup = groups.find(
    (group) =>
      originalDestinationIndex > group.startIndex &&
      originalDestinationIndex < group.endIndexExclusive
  );

  if (!containingGroup) {
    return rawDestinationIndex;
  }

  const movingDown = rawDestinationIndex > sourceIndex;
  const normalizedOriginalDestinationIndex = movingDown
    ? containingGroup.endIndexExclusive
    : containingGroup.startIndex;

  return normalizedOriginalDestinationIndex > sourceIndex
    ? normalizedOriginalDestinationIndex - 1
    : normalizedOriginalDestinationIndex;
};

const getDragPreviewState = (insights = [], sourceIndex, destinationIndex) => {
  const draggedBlock = getDraggedInsightBlock(insights, sourceIndex);
  const sourceId = draggedBlock.insights[0]?.id || null;
  const blockIds = draggedBlock.insights.map((insight) => insight.id);
  const isDestinationInsideBlock =
    destinationIndex !== null &&
    destinationIndex >= draggedBlock.startIndex &&
    destinationIndex < draggedBlock.endIndexExclusive;
  const renderInsertionIndex =
    destinationIndex === null || isDestinationInsideBlock
      ? null
      : destinationIndex > draggedBlock.startIndex
      ? destinationIndex + 1
      : destinationIndex;

  return {
    sourceIndex,
    sourceId,
    destinationIndex,
    renderInsertionIndex,
    blockIds,
    blockLength: draggedBlock.insights.length,
    isGroupedMove: draggedBlock.isGroupedMove,
  };
};

const renderOutlineInsightTypeMarker = (insightType, size = 18) => {
  const marker =
    Number.isFinite(Number(insightType)) && outlineInsightTypeMarkerMap[Number(insightType)]
      ? outlineInsightTypeMarkerMap[Number(insightType)]
      : null;

  if (!marker) {
    return null;
  }

  const MarkerIcon = marker.kind === "icon" ? marker.value : null;

  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: marker.color,
        color: "#ffffff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.55),
        fontWeight: 700,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {MarkerIcon ? (
        <MarkerIcon fontSize="inherit" />
      ) : (
        <Box component="span" sx={marker.textSx}>
          {marker.value}
        </Box>
      )}
    </Box>
  );
};

const countWords = (text = "") => {
  const matches = String(text).trim().match(/\b[\w'-]+\b/g);
  return matches ? matches.length : 0;
};

const getEstimatedGuideDurationSeconds = (insights = []) => {
  if (!Array.isArray(insights) || insights.length === 0) {
    return 0;
  }

  const contentSeconds = insights.reduce((sum, insight) => {
    const wordCount = countWords(insight?.text || "");
    const readingSeconds = Math.ceil((wordCount / GUIDE_READING_WORDS_PER_MINUTE) * 60);
    const baseInsightSeconds = readingSeconds + GUIDE_BASE_SECONDS_PER_INSIGHT;
    const multiplier = Number(insight?.insightType) === 2 ? GUIDE_ASSESSMENT_TIME_MULTIPLIER : 1;

    return sum + Math.ceil(baseInsightSeconds * multiplier);
  }, 0);

  const environmentSequence = insights
    .map((insight) =>
      Array.isArray(insight?.insightTriggers)
        ? insight.insightTriggers.find((trigger) => Number(trigger?.insightTriggerType) === 1)?.triggerId || ""
        : ""
    )
    .filter(Boolean);

  const environmentChangeCount = environmentSequence.reduce((count, environmentId, index) => {
    if (index === 0) {
      return count;
    }

    return environmentId !== environmentSequence[index - 1] ? count + 1 : count;
  }, 0);

  return contentSeconds + environmentChangeCount * GUIDE_ENVIRONMENT_CHANGE_SECONDS;
};

const formatDurationLabel = (seconds) => {
  const roundedSeconds = Math.max(0, Math.ceil(seconds / 5) * 5);
  const minutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;

  if (minutes > 0 && remainingSeconds > 0) {
    return translate("{value0} min {value1} sec", {
  value0: minutes,
  value1: remainingSeconds
});
  }

  if (minutes > 0) {
    return translate("{value0} min", {
  value0: minutes
});
  }

  return translate("{value0} sec", {
  value0: remainingSeconds
});
};


const flattenGuideData = (guide) => {
  const insightDatasUpdated = Array.isArray(guide.insightDatas)
    ? guide.insightDatas
        .filter((item) => item !== null)
        .map((item) => item?.data || item)
        .filter(Boolean)
    : [];
  return {
    ...guide,
    insightDatas: orderGuideInsights(insightDatasUpdated),
    elementLabels: cloneGuideElementLabels(guide.elementLabels),
    environmentLabels: cloneGuideEnvironmentLabels(guide.environmentLabels),
    actionButtonLabels: cloneGuideActionButtonLabels(guide.actionButtonLabels),
  };
};

const buildGuideOnlyPayload = (guide) => {
  const guidePayload = {
    ...guide,
    insightDataIds: Array.isArray(guide.insightDataIds)
      ? guide.insightDataIds
      : (guide.insightDatas || []).map((insight) => insight.id),
  };
  delete guidePayload.insightDatas;
  return guidePayload;
};


const GuideEditor = ({ initialGuide, defaultSystemOptions, isNewGuide = false }) => {
  const locale = useLocale();
  const [guide, setGuide] = useState({
    name: "New Guide",
    id: generateId(),
    insightDatas: [],
    insightDataIds: [],
    elementLabels: [],
    environmentLabels: [],
    actionButtonLabels: [],
  });

  const [systemData, setSystemData] = useState(null);
  const { getItem, getThumbnail } = useContent();
  const [environments, setEnvironments] = useState([]);
  const [elements, setElements] = useState([]);
  const { postGuide } = useExperiences();
  const insightRefs = useRef({});
  const guideRef = useRef(guide);
  const getItemRef = useRef(getItem);
  const postGuideRef = useRef(postGuide);
  const systemLoadRequestRef = useRef(0);
  const persistedContextIdRef = useRef(initialGuide?.contextId || "");
  const allowInitialAutoPersistRef = useRef(!isNewGuide && Boolean(initialGuide?.contextId));

  const [guideStatus, setGuideStatus] = useState(null); // e.g., "Saving guide…", "Guide saved ✅"
  const [savingGuide, setSavingGuide] = useState(false);
  const [savingInsights, setSavingInsights] = useState(false);
  const [insightStatus, setInsightStatus] = useState(null); // e.g., "Uploading insight 2/5…", "All insights uploaded ✅"
  const [doneStatus, setDoneStatus] = useState(null); // e.g., "Submitted successfully!"
  const [exportingDocx, setExportingDocx] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [activeInsightId, setActiveInsightId] = useState(null);
  const [expandRequest, setExpandRequest] = useState(null);
  const [outlineEnvironmentThumbnails, setOutlineEnvironmentThumbnails] = useState({});
  const [dragPreview, setDragPreview] = useState(null);
  const [originalElementLabels, setOriginalElementLabels] = useState([]);
  const [originalEnvironmentLabels, setOriginalEnvironmentLabels] = useState([]);
  const [originalActionButtonLabels, setOriginalActionButtonLabels] = useState([]);
  const [elementLabelsDialogOpen, setElementLabelsDialogOpen] = useState(false);
  const [systemLabelLoadNonce, setSystemLabelLoadNonce] = useState(0);
  const [systemLabelLoadState, setSystemLabelLoadState] = useState({
    status: "idle",
    error: "",
  });
  const [elementLabelStatus, setElementLabelStatus] = useState(null);
  const [insertMenuState, setInsertMenuState] = useState({
    anchorEl: null,
    insertionIndex: null,
  });
  const estimatedGuideDurationLabel = useMemo(
    () => formatDurationLabel(getEstimatedGuideDurationSeconds(guide.insightDatas)),
    [guide.insightDatas, locale]
  );
  const outlineGroups = useMemo(
    () => buildGuideOutlineGroups(guide.insightDatas, environments),
    [guide.insightDatas, environments, locale]
  );
  const outlineEnvironmentIds = useMemo(
    () => Array.from(new Set(outlineGroups.map((group) => group.environmentId).filter(Boolean))),
    [outlineGroups, locale]
  );
  const outlineEnvironmentIdsKey = useMemo(
    () => outlineEnvironmentIds.join("|"),
    [outlineEnvironmentIds, locale]
  );
  const guideElementLabelValidationErrors = useMemo(
    () => [
      ...validateGuideElementLabels(guide.elementLabels),
      ...validateGuideEnvironmentLabels(guide.environmentLabels),
      ...validateGuideActionButtonLabels(guide.actionButtonLabels),
    ],
    [guide.actionButtonLabels, guide.elementLabels, guide.environmentLabels, locale]
  );
  const elementLabelsReady =
    Boolean(guide.contextId) && systemLabelLoadState.status === "ready";
  const elementLabelSaveBlocked =
    Boolean(guide.contextId) &&
    (!elementLabelsReady || guideElementLabelValidationErrors.length > 0);

  useEffect(() => {
    guideRef.current = guide;
  }, [guide]);

  useEffect(() => {
    getItemRef.current = getItem;
  }, [getItem]);

  useEffect(() => {
    postGuideRef.current = postGuide;
  }, [postGuide]);

  const startStatuses = () => {
    setDoneStatus(null);
    setGuideStatus(null);
    setInsightStatus(null);
  };

  const finishStatuses = () => {
    setGuideStatus(null);
    setInsightStatus(null);
    setDoneStatus(translate("Submitted successfully!"));
    // Optionally auto-hide after a few seconds:
    // setTimeout(() => setDoneStatus(null), 3000);
  };
  useEffect(() => {
    if (initialGuide) {
      setGuide(flattenGuideData(initialGuide));
    }
  }, [initialGuide]);

  useEffect(() => {
    const insightIds = guide.insightDatas.map((insight) => insight.id);

    if (insightIds.length === 0) {
      if (activeInsightId !== null) {
        setActiveInsightId(null);
      }
      return;
    }

    if (!activeInsightId || !insightIds.includes(activeInsightId)) {
      setActiveInsightId(insightIds[0]);
    }
  }, [guide.insightDatas, activeInsightId]);

  useEffect(() => {
    let cancelled = false;
    const environmentIds = outlineEnvironmentIdsKey ? outlineEnvironmentIdsKey.split("|") : [];

    if (environmentIds.length === 0) {
      setOutlineEnvironmentThumbnails({});
      return () => {
        cancelled = true;
      };
    }

    Promise.all(
      environmentIds.map(async (environmentId) => {
        try {
          const thumbnailUrl = await getThumbnail(environmentId);
          return [environmentId, thumbnailUrl || ""];
        } catch {
          return [environmentId, ""];
        }
      })
    ).then((entries) => {
      if (!cancelled) {
        setOutlineEnvironmentThumbnails(Object.fromEntries(entries));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [getThumbnail, outlineEnvironmentIdsKey]);

  const updateAutoCompleteTriggers = useCallback((insights) => {
    console.log("3 - UPDATE AUTO COMPLETE TRIGGER", insights);
    const systemStartEnvironment = resolveSystemStartEnvironment(systemData);

    return orderGuideInsights(insights).map((insight, insightIdx, arr) => {
      if (isIdleInsight(insight) || !Array.isArray(insight.insightTriggers) || insight.insightTriggers.length === 0) {
        return insight;
      }

      const previousInsight = getPreviousOrdinaryInsight(arr, insightIdx);
      let firstType2Handled = false;

      const updatedTriggers = insight.insightTriggers.map((trigger) => {
        if (!previousInsight && trigger.insightTriggerType === 1) {
          if (!trigger.triggerId && systemStartEnvironment.id) {
            return {
              ...trigger,
              triggerId: systemStartEnvironment.id,
              name: systemStartEnvironment.name || trigger.name || "",
            };
          }

          if (trigger.triggerId === systemStartEnvironment.id && !trigger.name) {
            return {
              ...trigger,
              name: systemStartEnvironment.name || "",
            };
          }

          return trigger;
        }

        if (trigger.insightTriggerType === 2) {
          if (!firstType2Handled) {
            firstType2Handled = true;

            if (previousInsight) {
              return {
                ...trigger,
                triggerId: previousInsight.id,
                name: previousInsight.name || translate("Previous Insight"),
              };
            }

            return { ...trigger, triggerId: "", name: "" };
          }

          return trigger;
        }

        return trigger;
      });

      return { ...insight, insightTriggers: updatedTriggers };
    });
  }, [systemData, locale]);

  useEffect(() => {
    if (!guide.contextId) {
      systemLoadRequestRef.current += 1;
      setSystemData(null);
      setOriginalElementLabels([]);
      setOriginalEnvironmentLabels([]);
      setOriginalActionButtonLabels([]);
      setEnvironments([]);
      setElements([]);
      setSystemLabelLoadState({ status: "idle", error: "" });
      setElementLabelStatus(null);
      setElementLabelsDialogOpen(false);
      return;
    }

    const selectedSystemId = guide.contextId;
    const requestId = systemLoadRequestRef.current + 1;
    systemLoadRequestRef.current = requestId;
    let cancelled = false;

    setSystemData(null);
    setOriginalElementLabels([]);
    setOriginalEnvironmentLabels([]);
    setOriginalActionButtonLabels([]);
    setEnvironments([]);
    setElements([]);
    setSystemLabelLoadState({ status: "loading", error: "" });
    setElementLabelStatus(null);
    setElementLabelsDialogOpen(false);

    const loadSystemLabels = async () => {
      try {
        const data = await getItemRef.current("systems", selectedSystemId, true, {
          forceRefresh: true,
        });

        if (cancelled || requestId !== systemLoadRequestRef.current) {
          return;
        }

        if (!data) {
          throw new Error(translate("The selected System could not be loaded."));
        }

        const extraction = extractSystemGuideLabels(data);
        if (extraction.errors.length > 0) {
          setSystemLabelLoadState({
            status: "error",
            error: extraction.errors.slice(0, 3).join(" "),
          });
          return;
        }

        const currentGuide = guideRef.current;
        if (currentGuide.contextId !== selectedSystemId) {
          return;
        }

        const elementReconciliation = reconcileGuideElementLabels(
          currentGuide.elementLabels,
          extraction.elementLabels
        );
        const environmentReconciliation = reconcileGuideEnvironmentLabels(
          currentGuide.environmentLabels,
          extraction.environmentLabels
        );
        const actionButtonReconciliation = reconcileGuideActionButtonLabels(
          currentGuide.actionButtonLabels,
          extraction.actionButtonLabels
        );
        const validationErrors = [
          ...elementReconciliation.validationErrors,
          ...environmentReconciliation.validationErrors,
          ...actionButtonReconciliation.validationErrors,
        ];
        const reconciliationChanged =
          elementReconciliation.changed ||
          environmentReconciliation.changed ||
          actionButtonReconciliation.changed;

        setSystemData(data);
        setOriginalElementLabels(extraction.elementLabels);
        setOriginalEnvironmentLabels(extraction.environmentLabels);
        setOriginalActionButtonLabels(extraction.actionButtonLabels);
        setSystemLabelLoadState({ status: "ready", error: "" });

        if (validationErrors.length > 0) {
          allowInitialAutoPersistRef.current = false;
          return;
        }

        const reconciledGuide = reconciliationChanged
          ? {
              ...currentGuide,
              elementLabels: elementReconciliation.elementLabels,
              environmentLabels: environmentReconciliation.labels,
              actionButtonLabels: actionButtonReconciliation.labels,
            }
          : currentGuide;

        if (reconciliationChanged) {
          setGuide(reconciledGuide);

          const addedSummary = [
            elementReconciliation.addedElementCount
              ? translate("Elements: {value0}", {value0: elementReconciliation.addedElementCount})
              : "",
            elementReconciliation.addedSubElementCount
              ? translate("Sub-elements: {value0}", {value0: elementReconciliation.addedSubElementCount})
              : "",
            environmentReconciliation.addedCount
              ? translate("Environments: {value0}", {value0: environmentReconciliation.addedCount})
              : "",
            actionButtonReconciliation.addedCount
              ? translate("Action buttons: {value0}", {value0: actionButtonReconciliation.addedCount})
              : "",
          ]
            .filter(Boolean)
            .join(translate(" and "));
          const shouldAutoPersist =
            allowInitialAutoPersistRef.current &&
            !isNewGuide &&
            selectedSystemId === persistedContextIdRef.current;

          if (shouldAutoPersist) {
            try {
              await postGuideRef.current(buildGuideOnlyPayload(reconciledGuide));
              if (!cancelled && requestId === systemLoadRequestRef.current) {
                setElementLabelStatus({
                  severity: "success",
                  message: translate("Added and saved {value0} from the current System.", {
  value0: addedSummary
}),
                });
              }
            } catch (error) {
              console.error("Unable to save reconciled guide labels:", error);
              if (!cancelled && requestId === systemLoadRequestRef.current) {
                setElementLabelStatus({
                  severity: "warning",
                  message: translate("Added {value0}, but the automatic save failed. Use Save Guide to retry.", {
  value0: addedSummary
}),
                });
              }
            }
          } else {
            setElementLabelStatus({
              severity: "info",
              message: translate("Added {value0}. Use Save Guide to commit the updated label list.", {
  value0: addedSummary
}),
            });
          }
        }

        allowInitialAutoPersistRef.current = false;
      } catch (error) {
        console.error("Unable to load System labels:", error);
        if (!cancelled && requestId === systemLoadRequestRef.current) {
          setSystemLabelLoadState({
            status: "error",
            error: error?.message || translate("The selected System could not be loaded."),
          });
        }
      }
    };

    loadSystemLabels();

    return () => {
      cancelled = true;
    };
  }, [guide.contextId, isNewGuide, systemLabelLoadNonce, locale]);


  useEffect(() => {
    if (!systemData) {
      setEnvironments([]);
      setElements([]);
      return;
    }

    setEnvironments(getSystemEnvironmentEntries(systemData));
    setElements(
      originalElementLabels.map((elementLabel) => ({
        id: elementLabel.elementId,
        name: elementLabel.displayName,
      }))
    );

    if (guide.insightDatas?.length) {
      const updatedInsights = updateAutoCompleteTriggers(guide.insightDatas);
      if (JSON.stringify(updatedInsights) !== JSON.stringify(guide.insightDatas)) {
        setGuide((previousGuide) => ({
          ...previousGuide,
          insightDatas: updatedInsights,
        }));
      }
    }
  }, [guide.insightDatas, originalElementLabels, systemData, updateAutoCompleteTriggers]);


  

  // Called when an insight changes
  const handleInsightChange = (index, updatedInsight) => {
    // console.log(guide)
    const newInsights = [...guide.insightDatas];
    newInsights[index] = updatedInsight;
    // Auto-update triggers for all insights.
        console.log(" INSIGHTs changed", newInsights);

    const updatedInsights = updateAutoCompleteTriggers(newInsights);
    setGuide({ ...guide, insightDatas: updatedInsights });
  };

  const handleCloneInsight = (index) => {
    const source = guide.insightDatas[index];
    console.log("CLONING INSIGHT", source)
    // deep-copy the object and give it a fresh ID
    const clone = {
      ...JSON.parse(JSON.stringify(source)),
      id: generateId(),
      name: `${source.name || translate("Untitled")} (Copy)`,
    };

    const newInsights = [...guide.insightDatas];
    newInsights.splice(index + 1, 0, clone); // insert just after original

    

    console.log("NEW INSIGHTS", newInsights)
    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
    setActiveInsightId(clone.id);
    setExpandRequest({ id: clone.id, nonce: Date.now() });
  };

  const insertInsightAt = (insertionIndex, defaultTrigger) => {
    const newInsight = createInsightDraft(defaultTrigger);
    const newInsights = [...guide.insightDatas];

    newInsights.splice(insertionIndex, 0, newInsight);

    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
    setActiveInsightId(newInsight.id);
    setExpandRequest({ id: newInsight.id, nonce: Date.now() });
  };

  const isGroupBoundaryInsertionIndex = (insertionIndex) => {
    if (insertionIndex <= 0 || insertionIndex >= guide.insightDatas.length) {
      return true;
    }

    return Boolean(getEnvironmentVisitedTrigger(guide.insightDatas[insertionIndex]));
  };

  const closeInsertMenu = () => {
    setInsertMenuState({
      anchorEl: null,
      insertionIndex: null,
    });
  };

  const handleInsertLinkedInsightAt = (insertionIndex) => {
    closeInsertMenu();
    insertInsightAt(insertionIndex, createDefaultInsightCompleteTrigger());
  };

  const handleInsertEnvironmentInsightAt = (insertionIndex) => {
    closeInsertMenu();
    insertInsightAt(
      insertionIndex,
      insertionIndex === 0
        ? createDefaultEnvironmentVisitedTrigger(systemData)
        : createBlankEnvironmentVisitedTrigger()
    );
  };

  const handleInsertControlClick = (event, insertionIndex) => {
    if (guide.insightDatas.length === 0 || insertionIndex === 0) {
      handleInsertEnvironmentInsightAt(insertionIndex);
      return;
    }

    if (!isGroupBoundaryInsertionIndex(insertionIndex)) {
      handleInsertLinkedInsightAt(insertionIndex);
      return;
    }

    setInsertMenuState({
      anchorEl: event.currentTarget,
      insertionIndex,
    });
  };

  // Add a new insight (with default values)
  const handleAddInsight = () => {
    insertInsightAt(
      guide.insightDatas.length,
      guide.insightDatas.length === 0
        ? createDefaultEnvironmentVisitedTrigger(systemData)
        : createDefaultInsightCompleteTrigger()
    );
  };

  // Delete an insight and recalc auto-set triggers.
  const handleDeleteInsight = (index) => {
    const newInsights = [...guide.insightDatas];
    newInsights.splice(index, 1);
    console.log("DELETE INSIGHT", newInsights)
    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
  };

  // When the drag-and-drop operation ends, reorder the list and update auto-set triggers.
  const onDragEnd = (result) => {
    const previewBeforeDrop = dragPreview;
    setDragPreview(null);

    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = normalizeGroupedDragDestinationIndex(
      guide.insightDatas,
      sourceIndex,
      result.destination.index
    );
    const newInsights = Array.from(guide.insightDatas);
    const draggedBlock = getDraggedInsightBlock(newInsights, sourceIndex);

    if (draggedBlock.insights.length === 0) {
      return;
    }

    if (
      destinationIndex >= draggedBlock.startIndex &&
      destinationIndex < draggedBlock.endIndexExclusive
    ) {
      return;
    }

    newInsights.splice(
      draggedBlock.startIndex,
      draggedBlock.endIndexExclusive - draggedBlock.startIndex
    );

    const insertionIndex =
      destinationIndex > draggedBlock.startIndex
        ? destinationIndex - ((draggedBlock.endIndexExclusive - draggedBlock.startIndex) - 1)
        : destinationIndex;

    const draggedParentId = draggedBlock.insights[0]?.id;
    const draggedParentTopBefore =
      draggedParentId && previewBeforeDrop?.isGroupedMove
        ? insightRefs.current[draggedParentId]?.getBoundingClientRect().top ?? null
        : null;

    newInsights.splice(insertionIndex, 0, ...draggedBlock.insights);

    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
    if (draggedParentId) {
      setActiveInsightId(draggedParentId);
    }

    if (draggedParentId && draggedParentTopBefore !== null && previewBeforeDrop?.isGroupedMove) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const draggedParentNode = insightRefs.current[draggedParentId];
          if (!draggedParentNode) {
            return;
          }

          const draggedParentTopAfter = draggedParentNode.getBoundingClientRect().top;
          window.scrollBy(0, draggedParentTopAfter - draggedParentTopBefore);
        });
      });
    }
  };

  const handleOutlineSelect = useCallback((insightId) => {
    setActiveInsightId(insightId);
    setExpandRequest({ id: insightId, nonce: Date.now() });

    const insightNode = insightRefs.current[insightId];
    if (insightNode) {
      insightNode.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleDragStart = useCallback((start) => {
    setDragPreview(getDragPreviewState(guide.insightDatas, start.source.index, null));
  }, [guide.insightDatas]);

  const handleDragUpdate = useCallback((update) => {
    const normalizedDestinationIndex = update.destination
      ? normalizeGroupedDragDestinationIndex(
          guide.insightDatas,
          update.source.index,
          update.destination.index
        )
      : null;

    setDragPreview(
      getDragPreviewState(
        guide.insightDatas,
        update.source.index,
        normalizedDestinationIndex
      )
    );
  }, [guide.insightDatas]);

  const prepareInsightForSave = (raw) => ({
    ...raw,
    startConditions: normaliseCondArray(raw.startConditions),
    startActions: Array.isArray(raw.startActions)
      ? raw.startActions.map((action) => ({
          ...action,
          actionType: Number.isFinite(Number(action.actionType))
            ? Number(action.actionType)
            : 0,
          targetId: String(action.targetId || "").trim(),
          value: String(action.value ?? "").trim(),
        }))
      : [],
    endConditions: normaliseCondArray(raw.endConditions),
  });

  const renderInsertionIndex = dragPreview?.renderInsertionIndex ?? null;
  const groupedDragChildCount = dragPreview?.isGroupedMove ? Math.max(0, dragPreview.blockLength - 1) : 0;

  const renderDragInsertionPreview = () => {
    if (renderInsertionIndex === null) {
      return null;
    }

    return (
      <Box
        sx={{
          ml: "32px",
          mr: 1,
          mb: 1,
        }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: 2,
              borderRadius: 999,
              backgroundColor: "var(--color-action-primary, #239fc6)",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--color-action-primary, #239fc6)",
              whiteSpace: "nowrap",
            }}
          >
            {dragPreview?.isGroupedMove
              ? translate("Drop {value0} linked insights here", {
  value0: dragPreview.blockLength
})
              : translate("Drop insight here")}
          </Typography>
        </Box>
      </Box>
    );
  };

  const normaliseCondArray = (arr = []) =>
    arr.map((c) => {
      // keep environment IDs as strings
      if ([0, 1, 12].includes(Number(c.InsightConditionType))) {
        return {
          ...c,
          conditions: Array.isArray(c.conditions)
            ? c.conditions
            : c.conditions
            ? [c.conditions] // wrap the ID
            : [], // keep empty if nothing selected
        };
      }

      // all other types: split on '|'
      return {
        ...c,
        conditions: Array.isArray(c.conditions)
          ? c.conditions
          : c.conditions
              .split("|")
              .map((s) => s.trim())
              .filter(Boolean),
      };
    });

  const handleSystemChange = (newSystemId) => {
    allowInitialAutoPersistRef.current = false;
    setGuide((previousGuide) => ({
      ...previousGuide,
      contextId: newSystemId,
    }));
  };

  const handleRetrySystemLabels = () => {
    setSystemLabelLoadNonce((nonce) => nonce + 1);
  };

  const handleElementLabelsChange = (elementLabels) => {
    setGuide((previousGuide) => ({
      ...previousGuide,
      elementLabels,
    }));
  };

  const handleEnvironmentLabelsChange = (environmentLabels) => {
    setGuide((previousGuide) => ({
      ...previousGuide,
      environmentLabels,
      ...(Array.isArray(previousGuide.recoveryDestinations)
        ? {
            recoveryDestinations: updateRecoveryDestinationsForEnvironmentLabels(
              previousGuide.recoveryDestinations,
              previousGuide.environmentLabels,
              environmentLabels
            ),
          }
        : {}),
    }));
  };

  const handleActionButtonLabelsChange = (actionButtonLabels) => {
    setGuide((previousGuide) => ({
      ...previousGuide,
      actionButtonLabels,
    }));
  };

  // When the guide is submitted, prepare the payload as before.
  const handleSubmit = async () => {
    if (elementLabelSaveBlocked) {
      startStatuses();
      setGuideStatus(
        guideElementLabelValidationErrors[0] ||
          translate("Wait for the selected System's element labels to finish loading before saving.")
      );
      return;
    }

    const preparedInsights = updateAutoCompleteTriggers(guide.insightDatas).map(prepareInsightForSave);
    const guidePayload = {
      ...guide,
      insightDataIds: preparedInsights.map((i) => i.id),
    };
    delete guidePayload.insightDatas;

    try {
      startStatuses();

      // 1) Save guide
      setGuideStatus(translate("Saving guide\u2026"));
      setSavingGuide(true);
      try {
        await postGuide(guidePayload); // uses context; no refetch needed
        setGuideStatus(translate("Saving Guide..."));
        setElementLabelStatus(null);
      } catch (error) {
        setGuideStatus(translate("Guide save failed \u274C"));
        console.error("Error saving guide:", error);
        return; // stop if guide failed to save
      } finally {
        setSavingGuide(false);
      }

      // 2) Save insights (one by one with progress)
      const total = preparedInsights.length;
      setSavingInsights(total > 0);
      for (let i = 0; i < total; i++) {
        setInsightStatus(translate("[{value0}/{value1}] Uploading insight {value2} \u2026", {
  value0: i + 1,
  value1: total,
  value2: preparedInsights[i].name
}));
        try {
          await ExperiencesAPI.post("insights", { ...preparedInsights[i] }, { requiresAuth: true });
        } catch (error) {
          console.error(`Error creating insight ${i + 1}:`, error);
          setInsightStatus(translate("Insight {value0} failed \u274C", {
  value0: i + 1
}));
          // continue or break — your call. Here we continue:
        }
      }
      if (total > 0) setInsightStatus(translate("Guide saved. All insights uploaded \u2705"));

      // 3) Done
      finishStatuses();
    } catch (error) {
      console.error("Submission error:", error);
      setGuideStatus(translate("Submission failed \u274C"));
    } finally {
      setSavingInsights(false);
    }
  };

  const handleExportDocx = async () => {
    const thumbnailUrls = { ...outlineEnvironmentThumbnails };

    try {
      setExportingDocx(true);
      setExportStatus(null);

      const missingThumbnailEntries = await Promise.all(
        outlineEnvironmentIds
          .filter((environmentId) => !thumbnailUrls[environmentId])
          .map(async (environmentId) => {
            try {
              return [environmentId, await getThumbnail(environmentId)];
            } catch {
              return [environmentId, ""];
            }
          })
      );

      missingThumbnailEntries.forEach(([environmentId, thumbnailUrl]) => {
        if (thumbnailUrl) {
          thumbnailUrls[environmentId] = thumbnailUrl;
        }
      });

      if (missingThumbnailEntries.some(([, thumbnailUrl]) => thumbnailUrl)) {
        setOutlineEnvironmentThumbnails((previousThumbnails) => ({
          ...previousThumbnails,
          ...Object.fromEntries(missingThumbnailEntries.filter(([, thumbnailUrl]) => thumbnailUrl)),
        }));
      }

      await downloadGuideDocx({
        guide,
        environments,
        systemData,
        thumbnailUrls,
      });
      setExportStatus(translate("DOCX exported."));
    } catch (error) {
      console.error("Guide DOCX export failed:", error);
      setExportStatus(translate("DOCX export failed."));
    } finally {
      setExportingDocx(false);
    }
  };


  return (
    <Container sx={{ marginTop: 2 }}>
      <Paper
        elevation={1}
        sx={{
          position: "sticky",
          top: { xs: 72, md: 88 },
          zIndex: 20,
          mb: 2,
          px: 1.5,
          py: 1.25,
          border: "1px solid var(--color-header-border, var(--color-border-subtle, rgba(0, 0, 0, 0.12)))",
          backgroundColor: "var(--color-header-bg, var(--color-bg-card, rgba(255, 255, 255, 0.94)))",
          backdropFilter: "blur(8px)",
          boxShadow: "var(--shadow-elevation-header, 0 10px 36px rgba(15, 23, 42, 0.035))",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(260px, 1.5fr) minmax(220px, 1fr) auto",
            },
            gap: 1,
            alignItems: "start",
          }}
        >
          <AppTextField
            label={translate("Guide Name")}
            value={guide.name}
            onChange={(e) =>
              setGuide((previousGuide) => ({
                ...previousGuide,
                name: e.target.value,
              }))
            }
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <SystemOptionsDropdown
            value={guide.contextId || ""}
            onChange={handleSystemChange}
            defaultOptions={defaultSystemOptions}
            label={translate("System")}
            margin="none"
            size="small"
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", md: "flex-end" },
              gap: 1,
              flexWrap: "wrap",
              minHeight: 40,
            }}
          >
            <Tooltip
              title={translate("Estimated from script length, number of insights, assessment response time, and environment changes.")}
              arrow
              enterTouchDelay={0}
              componentsProps={tooltipSurfaceProps}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 999,
                  border: "1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.12))",
                  backgroundColor: "var(--color-bg-subtle, rgba(0, 0, 0, 0.03))",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  cursor: "help",
                }}
              >
                <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                  ~{estimatedGuideDurationLabel}
                </Typography>
              </Box>
            </Tooltip>

            <AppButton
              kind="secondary"
              onClick={() => setElementLabelsDialogOpen(true)}
              disabled={!elementLabelsReady}
              loading={systemLabelLoadState.status === "loading"}
              loadingLabel={translate("Labels")}
              startIcon={<LabelOutlinedIcon fontSize="small" />}
              className="guide-editor__element-label-action"
            >{translateRich("Labels ({value0})", {
  value0: originalElementLabels.length + originalEnvironmentLabels.length + originalActionButtonLabels.length
})}</AppButton>

            <AppButton
              kind="secondary"
              onClick={handleExportDocx}
              loading={exportingDocx}
              loadingLabel={translate("Exporting...")}
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                minHeight: 40,
                whiteSpace: "nowrap",
                px: 2,
              }}
            >{translate("Export DOCX")}</AppButton>

            <AppButton
              kind="primary"
              onClick={handleSubmit}
              disabled={elementLabelSaveBlocked}
              sx={{
                minHeight: 40,
                whiteSpace: "nowrap",
                px: 2,
              }}
            >{translate("Save Guide")}</AppButton>

            {exportStatus ? (
              <Typography
                variant="caption"
                sx={{
                  flexBasis: "100%",
                  textAlign: { xs: "left", md: "right" },
                  color: exportStatus.includes("failed") ? "error.main" : "text.secondary",
                }}
              >
                {exportStatus}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {systemLabelLoadState.status === "error" ? (
          <Alert
            severity="error"
            action={
              <AppButton kind="ghost" onClick={handleRetrySystemLabels}>{translate("Retry")}</AppButton>
            }
            className="guide-editor__element-label-alert"
          >
            {systemLabelLoadState.error}
          </Alert>
        ) : null}

        {elementLabelStatus ? (
          <Alert
            severity={elementLabelStatus.severity}
            className="guide-editor__element-label-alert"
          >
            {elementLabelStatus.message}
          </Alert>
        ) : null}

        {elementLabelsReady && guideElementLabelValidationErrors.length > 0 ? (
          <Alert severity="error" className="guide-editor__element-label-alert">
            {guideElementLabelValidationErrors[0]}
          </Alert>
        ) : null}
      </Paper>

      <Box
        sx={{
          display: { xs: "block", lg: "grid" },
          gridTemplateColumns: { lg: "260px minmax(0, 1fr)" },
          gap: { xs: 2, lg: 6 },
          alignItems: "start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: { xs: "none", lg: "block" },
            position: "sticky",
            top: 176,
            maxHeight: "calc(100vh - 196px)",
            overflow: "auto",
            pt: 0.25,
            pb: 0.25,
            pl: 0.25,
            pr: 2.5,
            mr: { lg: 5 },
            border: 0,
            backgroundColor: "transparent",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              px: 0.5,
              pb: 0.5,
              fontSize: 11,
              fontWeight: 600,
              color: "text.secondary",
              letterSpacing: "0.03em",
            }}
          >{translate("Outline")}</Typography>

          {outlineGroups.length > 0 ? (
            <Box sx={{ display: "grid", gap: 0.25 }}>
              {outlineGroups.map((group, groupIndex) => (
                <Box
                  key={group.key}
                  sx={{
                    pb: 0.25,
                    borderBottom:
                      groupIndex === outlineGroups.length - 1
                        ? "none"
                        : "1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.08))",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 0.5,
                      py: 0.5,
                    }}
                  >
                    {outlineEnvironmentThumbnails[group.environmentId] ? (
                      <Box
                        component="img"
                        src={outlineEnvironmentThumbnails[group.environmentId]}
                        alt={group.environmentName ? translate("{value0} thumbnail", {
  value0: group.environmentName
}) : translate("Environment thumbnail")}
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "5px",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 0,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {group.environmentName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        marginLeft: "auto",
                        fontSize: 10.5,
                        color: "text.secondary",
                        flexShrink: 0,
                      }}
                    >
                      {group.items.length}
                    </Typography>
                  </Box>

                  <Box sx={{ px: 0.25, pb: 0.25 }}>
                    {group.items.map((item) => {
                      const isActive = activeInsightId === item.id;

                      return (
                        <Box
                          key={item.id}
                          component="button"
                          type="button"
                          onClick={() => handleOutlineSelect(item.id)}
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 0.5,
                            py: 0.45,
                            pl: item.isNested ? 1.35 : 0.5,
                            border: 0,
                            borderRadius: "var(--radius-sm, 4px)",
                            backgroundColor: isActive
                              ? "var(--color-bg-highlight, rgba(0, 0, 0, 0.04))"
                              : "transparent",
                            color: "text.primary",
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "background-color 120ms ease",
                            "&:hover": {
                              backgroundColor: "var(--color-bg-highlight, rgba(0, 0, 0, 0.04))",
                            },
                          }}
                        >
                          {item.isNested ? (
                            <Typography
                              component="span"
                              sx={{
                                color: "text.secondary",
                                fontSize: 11.5,
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              ↳
                            </Typography>
                          ) : null}
                          {renderOutlineInsightTypeMarker(item.insightType, 14)}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: 12,
                                fontWeight: isActive ? 600 : 500,
                                color: "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1.25,
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" sx={{ px: 0.5, fontSize: 11.5, color: "text.secondary" }}>{translate("Add an insight to start outlining the guide.")}</Typography>
          )}
        </Paper>

        <Box sx={{ minWidth: 0 }}>
          <DragDropContext onDragStart={handleDragStart} onDragUpdate={handleDragUpdate} onDragEnd={onDragEnd}>
            <Droppable droppableId="insightList">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {guide.insightDatas.map((insight, index) => {
                    const isIncludedInGroupedDrag =
                      Boolean(dragPreview?.isGroupedMove) &&
                      dragPreview.blockIds.includes(insight.id) &&
                      dragPreview.sourceId !== insight.id;
                    const isGroupedDragParent = dragPreview?.sourceId === insight.id && dragPreview?.isGroupedMove;
                    const insertionIndex = index + 1;
                    const showInlineInsertControl =
                      !dragPreview && !isIdleInsight(insight) && index < guide.insightDatas.length - 1;
                    const insertAriaLabel = isGroupBoundaryInsertionIndex(insertionIndex)
                      ? translate("Insert insight here")
                      : translate("Insert linked insight here");

                    return (
                      <React.Fragment key={insight.id}>
                        {(index === 0 || (isIdleInsight(insight) && !isIdleInsight(guide.insightDatas[index - 1]))) && (
                          <Box className={`guide-editor__insight-section-heading${index === 0 ? " guide-editor__insight-section-heading--first" : ""}`}>
                            <Typography variant="h6" component="h2">
                              {isIdleInsight(insight) ? translate("Idle insights") : translate("Lesson insights")}
                            </Typography>
                            {isIdleInsight(insight) && (
                              <Typography variant="body2" color="text.secondary">{translate("Environment descriptions for idle recovery. Kept after the lesson insights.")}</Typography>
                            )}
                          </Box>
                        )}
                        {dragPreview && renderInsertionIndex === index ? renderDragInsertionPreview() : null}
                        <Draggable draggableId={insight.id} index={index}>
                          {(provided, snapshot) => (
                            <Box
                              component="div"
                              id={`guide-insight-${insight.id}`}
                              ref={(node) => {
                                provided.innerRef(node);
                                if (node) {
                                  insightRefs.current[insight.id] = node;
                                } else {
                                  delete insightRefs.current[insight.id];
                                }
                              }}
                              {...provided.draggableProps}
                              onMouseDownCapture={() => setActiveInsightId(insight.id)}
                              onFocusCapture={() => setActiveInsightId(insight.id)}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: 8,
                                display: "flex",
                                alignItems: "stretch",
                                gap: 8,
                                scrollMarginTop: 176,
                                opacity: isIncludedInGroupedDrag && !snapshot.isDragging ? 0.86 : 1,
                              }}
                              sx={{
                                position: "relative",
                                "&:hover .insight-inline-insert, &:focus-within .insight-inline-insert, & .insight-inline-insert-zone:hover .insight-inline-insert": {
                                  opacity: 1,
                                  pointerEvents: "auto",
                                  transform: "translateX(0)",
                                },
                              }}
                            >
                              <div
                                {...provided.dragHandleProps}
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 34,
                                  cursor: "grab",
                                  color: isIncludedInGroupedDrag
                                    ? "var(--color-action-primary, #239fc6)"
                                    : "var(--color-text-muted, rgba(0, 0, 0, 0.45))",
                                  userSelect: "none",
                                  fontWeight: 700,
                                }}
                              >
                                <strong>{isIncludedInGroupedDrag ? "↳" : "::"}</strong>
                                {isGroupedDragParent ? (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, 14px)",
                                      px: 0.75,
                                      py: 0.3,
                                      borderRadius: 999,
                                      backgroundColor: "var(--color-action-primary, #239fc6)",
                                      color: "var(--color-text-on-primary, #ffffff)",
                                      fontSize: 10.5,
                                      fontWeight: 700,
                                      lineHeight: 1.2,
                                      zIndex: 2,
                                      whiteSpace: "nowrap",
                                      pointerEvents: "none",
                                      boxShadow: "0 6px 14px rgba(0, 0, 0, 0.12)",
                                    }}
                                  >{translateRich("Linked insights: +{value0}", {value0: groupedDragChildCount})}</Box>
                                ) : null}
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  position: "relative",
                                  borderLeft: isIncludedInGroupedDrag
                                    ? "2px dashed var(--color-action-primary, #239fc6)"
                                    : "none",
                                  paddingLeft: isIncludedInGroupedDrag ? 6 : 0,
                                }}
                              >
                                {showInlineInsertControl ? (
                                  <Box
                                    className="insight-inline-insert-zone"
                                    sx={{
                                      position: "absolute",
                                      right: -92,
                                      top: 0,
                                      bottom: -12,
                                      width: 104,
                                      display: "flex",
                                      alignItems: "flex-end",
                                      justifyContent: "center",
                                      zIndex: 3,
                                    }}
                                  >
                                    <Box
                                      component="button"
                                      type="button"
                                      aria-label={insertAriaLabel}
                                      onClick={(event) => handleInsertControlClick(event, insertionIndex)}
                                      className="insight-inline-insert"
                                      sx={{
                                        appearance: "none",
                                        border: "1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.12))",
                                        backgroundColor: "var(--color-bg-card, #ffffff)",
                                        color: "text.secondary",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                                        minWidth: 26,
                                        height: 26,
                                        px: 0.75,
                                        borderRadius: 999,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.5,
                                        cursor: "pointer",
                                        opacity: insertMenuState.insertionIndex === insertionIndex ? 1 : 0,
                                        pointerEvents:
                                          insertMenuState.insertionIndex === insertionIndex ? "auto" : "none",
                                        transform:
                                          insertMenuState.insertionIndex === insertionIndex
                                            ? "translateX(0)"
                                            : "translateX(6px)",
                                        transition:
                                          "opacity 140ms ease, transform 140ms ease, color 140ms ease, border-color 140ms ease",
                                        "&:hover, &:focus-visible": {
                                          color: "text.primary",
                                          borderColor: "var(--color-action-primary, #239fc6)",
                                          outline: "none",
                                        },
                                        "& .insert-label": {
                                          display: "inline-block",
                                          maxWidth: 0,
                                          overflow: "hidden",
                                          whiteSpace: "nowrap",
                                          fontSize: 11,
                                          fontWeight: 600,
                                          opacity: 0,
                                          transition: "max-width 140ms ease, opacity 140ms ease",
                                        },
                                        "&:hover .insert-label, &:focus-visible .insert-label": {
                                          maxWidth: 72,
                                          opacity: 1,
                                        },
                                      }}
                                    >
                                      <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                                      <Box component="span" className="insert-label">{translate("Add insight")}</Box>
                                    </Box>
                                  </Box>
                                ) : null}
                                <InsightEditor
                                  key={insight.id}
                                  insight={insight}
                                  index={index}
                                  onChange={handleInsightChange}
                                  onClone={handleCloneInsight}
                                  onDelete={handleDeleteInsight}
                                  allInsights={guide.insightDatas}
                                  environments={environments}
                                  elements={elements}
                                  systemConditions={systemData?.conditionDatas || []}
                                  expandRequest={expandRequest}
                                />
                              </div>
                            </Box>
                          )}
                        </Draggable>
                      </React.Fragment>
                    );
                  })}
                  {dragPreview && renderInsertionIndex === guide.insightDatas.length ? renderDragInsertionPreview() : null}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Menu
            anchorEl={insertMenuState.anchorEl}
            open={Boolean(insertMenuState.anchorEl)}
            onClose={closeInsertMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem onClick={() => handleInsertLinkedInsightAt(insertMenuState.insertionIndex ?? 0)}>{translate("Add linked insight")}</MenuItem>
            <MenuItem onClick={() => handleInsertEnvironmentInsightAt(insertMenuState.insertionIndex ?? 0)}>{translate("Add environment section")}</MenuItem>
          </Menu>

          <AppButton kind="secondary" onClick={handleAddInsight} sx={{ marginRight: 2 }}>{translate("Add Insight")}</AppButton>
        </Box>
      </Box>

      {(guideStatus || insightStatus || doneStatus) && (
        <Box sx={{ position: "fixed", top: 180, right: 30, zIndex: 10000 }}>
          <Paper elevation={3} sx={{ p: 1.5, minWidth: 260 }}>
            {guideStatus && (
              <Box sx={{ display: "flex", alignItems: "center", mb: insightStatus || doneStatus ? 0.5 : 0 }}>
                {savingGuide && <CircularProgress size={16} sx={{ mr: 1 }} />}
                <Typography variant="body2">{guideStatus}</Typography>
              </Box>
            )}
            {insightStatus && (
              <Box sx={{ display: "flex", alignItems: "center", mb: doneStatus ? 0.5 : 0 }}>
                {savingInsights && <CircularProgress size={16} sx={{ mr: 1 }} />}
                <Typography variant="body2">{insightStatus}</Typography>
              </Box>
            )}
            {doneStatus && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {doneStatus}
                <IconButton
                  aria-label={translate("Close")}
                  size="small"
                  onClick={() => {
                    setDoneStatus(null);
                    setGuideStatus(null);
                    setInsightStatus(null);
                  }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 3,
                    height: "100%",
                    width: "40px",
                    // Give it a pill so it's visible on any bg
                    bgcolor: (theme) => theme.palette.action.hover,
                    "&:hover": { bgcolor: (theme) => theme.palette.action.selected },
                    color: "var(--color-text-secondary, rgba(0, 0, 0, 0.7))",
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      <ElementLabelsDialog
        open={elementLabelsDialogOpen}
        onClose={() => setElementLabelsDialogOpen(false)}
        onSave={handleSubmit}
        saveDisabled={elementLabelSaveBlocked}
        elementLabels={guide.elementLabels}
        originalElementLabels={originalElementLabels}
        environmentLabels={guide.environmentLabels}
        originalEnvironmentLabels={originalEnvironmentLabels}
        actionButtonLabels={guide.actionButtonLabels}
        originalActionButtonLabels={originalActionButtonLabels}
        onChange={handleElementLabelsChange}
        onEnvironmentLabelsChange={handleEnvironmentLabelsChange}
        onActionButtonLabelsChange={handleActionButtonLabelsChange}
      />
    </Container>
  );
};

export default GuideEditor;
